import {
  copyForm,
  createForm,
  destroyForm,
  getForm,
  getFormStats,
  updateFormTheme,
  updateUserFormAppSetting,
} from '@/lib/api/formApi'
import {
  FormAppSettingAttributes,
  FormAttributes,
  FormBasicFragment,
  GetFormQuery,
  Scene,
  ThemeAttributes,
} from '@/typings/domain'
import { ApolloError } from '@apollo/client'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { buildDefaultFormWithFieldsByScene } from '../utils/buildDefaultFormWithFieldsByScene'
import type { QueryState } from './queryStateType'

export type ShallowForm = Partial<Omit<NonNullable<GetFormQuery['form']>, 'billingAccount'>> & {
  token: string
  displayName: string
  billingAccountId?: string
  isCurrentUserManager?: boolean
} & {
  queryState?: QueryState
}

const formAdapter = createEntityAdapter<ShallowForm, string>({
  selectId: model => model.token,
})

export const formSlice = createSlice({
  name: 'forms',
  initialState: formAdapter.getInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(createFormThunk.fulfilled, (state, action) => {
        const createdForm = action.payload
        formAdapter.upsertOne(state, {
          ...mapToShallowForm(createdForm),
          queryState: {
            isLoading: false,
            loaded: true,
            error: null,
          },
        })
      })
      .addCase(fetchFormThunk.pending, (state, action) => {
        const formToken = action.meta.arg.formToken

        formAdapter.upsertOne(state, {
          token: formToken,
          displayName: state.entities[formToken]?.displayName ?? '',
          queryState: {
            isLoading: true,
            loaded: state.entities[formToken]?.queryState?.loaded ?? false,
            error: null,
          },
        })
      })
      .addCase(fetchFormThunk.fulfilled, (state, action) => {
        const fetchedForm = action.payload
        formAdapter.upsertOne(state, {
          ...mapToShallowForm(fetchedForm),
          queryState: {
            isLoading: false,
            loaded: true,
            error: null,
          },
        })
      })
      .addCase(fetchFormThunk.rejected, (state, action) => {
        const formToken = action.meta.arg.formToken
        const payload = action.payload as { message?: string; details?: QueryState['errorDetails'] } | undefined

        if (state.entities[formToken]?.queryState) {
          state.entities[formToken].queryState.isLoading = false
          state.entities[formToken].queryState.error = payload?.message ?? action.error.message
          state.entities[formToken].queryState.errorDetails = payload?.details
        }
      })
      .addCase(destroyFormThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg
        formAdapter.removeOne(state, formToken)
      })
      .addCase(copyFormThunk.fulfilled, (state, action) => {
        const { newForm: copiedForm } = action.payload
        formAdapter.addOne(state, {
          ...copiedForm,
          isCurrentUserManager: true,
        } as unknown as ShallowForm)
      })
      .addCase(fetchFormStatsThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg.formToken
        const stats = action.payload
        formAdapter.updateOne(state, { id: formToken, changes: { stats } })
      })
      .addCase(updateUserFormAppSettingThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg.formToken
        formAdapter.updateOne(state, { id: formToken, changes: { userFormAppSetting: action.payload } })
      })
  },
})

function mapToShallowForm(form: FormBasicFragment): ShallowForm {
  const { billingAccount, ...rest } = form
  return {
    ...rest,
    displayName: form.subname || form.name,
    billingAccountId: billingAccount.id,
    isCurrentUserManager: form.userFormRole?.originalName === 'manager',
  }
}

export type CreateFormThunkArgs = {
  folderToken?: string
  scene?: Maybe<Scene>
  createSource?: Maybe<string>
  createdFrom?: Maybe<string>
  aiMessageId?: Maybe<string>

  attributes?: FormAttributes
  themeAttributes?: ThemeAttributes
}

export const createFormThunk = createAsyncThunk('form/create', async (args: CreateFormThunkArgs, thunkAPI) => {
  let attributes = args.attributes
  if (!attributes) {
    const formWithFields = buildDefaultFormWithFieldsByScene(args.scene ?? Scene.Form)
    attributes = {
      name: formWithFields.name,
      description: formWithFields.description || undefined,
      fields: formWithFields.fields.length > 0 ? formWithFields.fields : undefined,
    } as FormAttributes
  }

  const { data } = await createForm({
    folderId: args.folderToken,
    scene: args.scene ?? Scene.Form,
    createdFrom: args.createdFrom,
    createSource: args.createSource ?? 'nextApp',
    aiMessageId: args.aiMessageId,
    attributes,
  })

  const createdForm = data?.createForm?.form
  if (!createdForm) {
    return thunkAPI.rejectWithValue({ message: 'Failed to create form!' })
  }

  const result = { ...createdForm }

  if (args.themeAttributes) {
    const { data } = await updateFormTheme({ formId: createdForm.token, attributes: args.themeAttributes })
    if (!data?.saveTheme?.theme) {
      return thunkAPI.rejectWithValue({ message: data?.saveTheme?.errors?.[0]?.message ?? 'Failed to save theme' })
    }
    result.theme = data.saveTheme.theme
  }

  return result
})

export const destroyFormThunk = createAsyncThunk('form/destroyForm', async (formToken: string, thunkAPI) => {
  const { data } = await destroyForm({ formToken })

  const error = data?.destroyForm?.errors?.[0]

  if (error) {
    return thunkAPI.rejectWithValue(error)
  }

  return formToken
})

export const copyFormThunk = createAsyncThunk('form/copyForm', async (formToken: string, thunkAPI) => {
  const { data } = await copyForm({ formToken })
  const error = data?.copyForm?.errors?.[0]
  if (error) {
    return thunkAPI.rejectWithValue(error)
  }

  const copy = data?.copyForm?.form
  return copy
    ? { copiedFromToken: formToken, newForm: copy }
    : thunkAPI.rejectWithValue({ message: 'Failed to copy form' })
})

export const fetchFormThunk = createAsyncThunk<NonNullable<GetFormQuery['form']>, { formToken: string }>(
  'forms/fetchOne',
  async ({ formToken }, thunkAPI) => {
    try {
      const { data } = await getForm({ formToken })
      return data.form ?? thunkAPI.rejectWithValue({ message: 'Failed to fetch form' })
    } catch (e) {
      const error = e instanceof ApolloError ? e.graphQLErrors[0] : undefined
      if (error) {
        const ext = error.extensions
        if (ext?.type === 'requireUserSwitch') {
          return thunkAPI.rejectWithValue({
            message: error.message,
            details: {
              type: 'requireUserSwitch',
              userId: ext.user_id as string,
              accountName: ext.account_name as string,
            },
          })
        }
        return thunkAPI.rejectWithValue({ message: error.message })
      }
      return thunkAPI.rejectWithValue({ message: e instanceof Error ? e.message : 'Unknown error' })
    }
  }
)

export const fetchFormStatsThunk = createAsyncThunk(
  'forms/fetchOneStats',
  async (variables: { formToken: string }, thunkAPI) => {
    const { data, errors } = await getFormStats(variables)
    return (
      data.form?.stats ??
      thunkAPI.rejectWithValue({
        message: errors?.[0]?.message,
      })
    )
  }
)

export const updateUserFormAppSettingThunk = createAsyncThunk(
  'forms/updateUserFormAppSetting',
  async (variables: { formToken: string; attributes: FormAppSettingAttributes }, thunkAPI) => {
    const { data, errors } = await updateUserFormAppSetting({
      id: variables.formToken,
      attributes: variables.attributes,
    })
    return (
      data?.updateUserFormAppSetting?.userFormAppSetting ?? thunkAPI.rejectWithValue({ message: errors?.[0]?.message })
    )
  }
)

export const formSelectors = formAdapter.getSelectors()

export default formSlice.reducer
